import { useState } from 'react'
import { motion } from 'framer-motion'
import { FaKey, FaUser, FaCog } from 'react-icons/fa'
import PageHeader from '@/components/PageHeader'
import AdminPageContainer from '@/components/AdminPageContainer'

interface Settings {
  username: string
  currentPassword: string
  newPassword: string
  confirmPassword: string
  siteTitle: string
  metaDescription: string
  googleAnalyticsId: string
}

const initialSettings: Settings = {
  username: 'admin',
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
  siteTitle: 'União Ferr - Soluções em Ferragens',
  metaDescription: 'Soluções em ferragens e materiais de construção em Araçoiaba da Serra/SP',
  googleAnalyticsId: '',
}

export default function AdminSettings() {
  const [settings, setSettings] = useState<Settings>(initialSettings)
  const [isSaving, setIsSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')

  const handleSave = async () => {
    setIsSaving(true)
    setSaveMessage('')

    try {
      // Validação de senha
      if (settings.newPassword || settings.confirmPassword) {
        if (!settings.currentPassword) {
          throw new Error('Senha atual é obrigatória para alterar a senha')
        }
        if (settings.newPassword !== settings.confirmPassword) {
          throw new Error('As novas senhas não coincidem')
        }
      }

      // Simulating an API call
      await new Promise((resolve) => setTimeout(resolve, 1000))
      setSaveMessage('Configurações atualizadas com sucesso!')
      setTimeout(() => setSaveMessage(''), 3000)
    } catch (error) {
      setSaveMessage(
        error instanceof Error ? error.message : 'Erro ao salvar configurações.'
      )
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <AdminPageContainer>
      <div className="space-y-6">
        <PageHeader title="Configurações" onSave={handleSave} isSaving={isSaving} />

        {saveMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-md p-4 ${
              saveMessage.includes('sucesso')
                ? 'bg-green-50 text-green-800'
                : 'bg-red-50 text-red-800'
            }`}
          >
            {saveMessage}
          </motion.div>
        )}

        <div className="grid gap-6 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-lg bg-white p-6 shadow"
          >
            <h3 className="mb-4 text-lg font-medium text-gray-900">
              Conta de Administrador
            </h3>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="username"
                  className="block text-sm font-medium text-gray-700"
                >
                  <FaUser className="mr-2 inline h-4 w-4" />
                  Nome de Usuário
                </label>
                <input
                  type="text"
                  id="username"
                  value={settings.username}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, username: e.target.value }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>

              <div>
                <label
                  htmlFor="currentPassword"
                  className="block text-sm font-medium text-gray-700"
                >
                  <FaKey className="mr-2 inline h-4 w-4" />
                  Senha Atual
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  value={settings.currentPassword}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      currentPassword: e.target.value,
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="Deixe em branco para manter a senha atual"
                />
              </div>

              <div>
                <label
                  htmlFor="newPassword"
                  className="block text-sm font-medium text-gray-700"
                >
                  <FaKey className="mr-2 inline h-4 w-4" />
                  Nova Senha
                </label>
                <input
                  type="password"
                  id="newPassword"
                  value={settings.newPassword}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      newPassword: e.target.value,
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="Deixe em branco para manter a senha atual"
                />
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700"
                >
                  <FaKey className="mr-2 inline h-4 w-4" />
                  Confirmar Nova Senha
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={settings.confirmPassword}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      confirmPassword: e.target.value,
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="Confirme a nova senha"
                />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-lg bg-white p-6 shadow"
          >
            <h3 className="mb-4 text-lg font-medium text-gray-900">
              Configurações do Site
            </h3>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="siteTitle"
                  className="block text-sm font-medium text-gray-700"
                >
                  <FaCog className="mr-2 inline h-4 w-4" />
                  Título do Site
                </label>
                <input
                  type="text"
                  id="siteTitle"
                  value={settings.siteTitle}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      siteTitle: e.target.value,
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>

              <div>
                <label
                  htmlFor="metaDescription"
                  className="block text-sm font-medium text-gray-700"
                >
                  <FaCog className="mr-2 inline h-4 w-4" />
                  Meta Descrição
                </label>
                <textarea
                  id="metaDescription"
                  rows={3}
                  value={settings.metaDescription}
                  onChange={(e) =>
                    setSettings((prev => ({
                      ...prev,
                      metaDescription: e.target.value,
                    })))
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>

              <div>
                <label
                  htmlFor="googleAnalyticsId"
                  className="block text-sm font-medium text-gray-700"
                >
                  <FaCog className="mr-2 inline h-4 w-4" />
                  ID do Google Analytics
                </label>
                <input
                  type="text"
                  id="googleAnalyticsId"
                  value={settings.googleAnalyticsId}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      googleAnalyticsId: e.target.value,
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="UA-XXXXXXXXX-X"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </AdminPageContainer>
  )
}
